import React from 'react';

import { offset } from '@floating-ui/react';
import { format, getYear, getMonth, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { isDate } from 'lodash-es';
import ReactDatePicker, {
  type DatePickerProps,
  registerLocale,
} from 'react-datepicker';

import ArrowIcon from '@/assets/icons/cal_arrow.svg?react';
import DatePickerInput from '@/components/ui/forms/DatePickerInput';
import useInputChangeValue from '@/hooks/common/useInputChangeValue';

import styles from './DatePicker.module.scss';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  innerRef?: React.Ref<HTMLInputElement>;
}

interface Props {
  valueFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  inputProps?: InputProps;
}

const CustomHeader: DatePickerProps['renderCustomHeader'] = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => (
  <div>
    <button
      type="button"
      onClick={decreaseMonth}
      disabled={prevMonthButtonDisabled}
      className={styles.button}
    >
      <ArrowIcon className={styles.arrowBack} />
    </button>
    <div className={styles.month}>
      <span>{getYear(date)}年</span>
      <span>{getMonth(date) + 1}月</span>
    </div>
    <button
      type="button"
      onClick={increaseMonth}
      disabled={nextMonthButtonDisabled}
      className={styles.button}
    >
      <ArrowIcon className={styles.arrowForward} />
    </button>
  </div>
);

const DatePicker: React.FC<Props> = ({
  valueFormat = 'yyyy-MM-dd',
  minDate = new Date(),
  maxDate,
  inputProps: { innerRef, onChange = () => void 0, ...inputProps } = {
    innerRef: undefined,
  },
}) => {
  const inputRef = React.useRef<HTMLInputElement>();
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  registerLocale('ja', ja);

  const { changeValue } = useInputChangeValue();

  const setDatePickerDate = React.useCallback(
    (date: Date | null) => {
      if (isDate(date) && inputRef.current) {
        setStartDate(date);
        const formatted = format(date, valueFormat);
        setSelectedDate(formatted);
        changeValue(inputRef.current, formatted);
      }
    },
    [changeValue, valueFormat],
  );

  // inputのvalueに値がある場合(componentの切り替えやback時)にDatePickerの表示側にも値をsetする
  React.useEffect(() => {
    if (inputRef.current?.value)
      setDatePickerDate(parseISO(inputRef.current.value));
  }, [inputRef.current?.value, setDatePickerDate]);

  return (
    <div className={styles.root} data-testid="datepicker">
      <input
        defaultValue={selectedDate || ''}
        {...inputProps}
        onChange={onChange} // onChangeが渡されていない場合のwarning抑制のためデフォルトのcallbackを指定しておく
        type="text"
        className={styles.hiddenInput}
        ref={(node) => {
          inputRef.current = node || undefined;
          if (typeof innerRef === 'function') {
            innerRef(node);
          } else if (innerRef) {
            (
              innerRef as React.MutableRefObject<HTMLInputElement | null>
            ).current = node;
          }
        }}
        data-testid="datepicker-value-input"
      />
      <ReactDatePicker
        locale="ja"
        renderCustomHeader={CustomHeader}
        dateFormat="yyyy年M月d日"
        selected={startDate}
        showPopperArrow={false}
        popperPlacement="bottom-end"
        popperModifiers={[
          offset({
            mainAxis: -2, // (-1 * 4px) / 2
          }),
        ]}
        customInput={
          <DatePickerInput
            isOpen={isOpen}
            data-testid="datepicker-custom-input"
          />
        }
        customInputRef="innerRef"
        minDate={minDate}
        maxDate={maxDate}
        onSelect={(date) => setDatePickerDate(date)}
        onChange={(date) => setDatePickerDate(date)}
        onCalendarOpen={() => {
          setIsOpen(true);
        }}
        onCalendarClose={() => {
          setIsOpen(false);
          // RHF validation をトリガーさせるため
          inputRef.current?.focus();
          inputRef.current?.blur();
        }}
        placeholderText="日付を選択してください"
      />
    </div>
  );
};

export default DatePicker;
